import React, { useState } from "react";
import {
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Box,
  Tooltip,
  alpha,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";

import {
  getFunderRates,
  deleteFunderRate,
} from "../../api/services/Billing/funderRates";
import { getFundersLookup } from "../../api/services/Billing/funder";
import { QK } from "../../base/qk";
import FunderRateDialog from "./Dialog/FunderRateDialog";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import DataTable from "../Table/DataTable";

const FunderRateCards = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [activeFunderId, setActiveFunderId] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const { data: rates = [], isLoading: ratesLoading } = useQuery(
    [QK.rateCards, "all"],
    () => getFunderRates()
  );

  const { data: fundersLookup = [], isLoading: fundersLoading } = useQuery(
    [QK.funders, "lookup"],
    () => getFundersLookup()
  );

  const deleteRateMutation = useMutation(deleteFunderRate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.rateCards, "all"]);
      enqueueSnackbar("Rate deleted successfully", { variant: "success" });
      setConfirmOpen(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete rate", { variant: "error" });
      setConfirmOpen(false);
    },
  });

  const getFunderName = (funderId) => {
    const funder = fundersLookup?.find((f) => f.id === funderId);
    return funder ? funder.name : `ID: ${funderId}`;
  };

  const handleAddRate = () => {
    setSelectedRate(null);
    setActiveFunderId(null);
    setDialogOpen(true);
  };

  const handleEditRate = (id) => {
    const rate = rates.find((r) => r.id === id);
    setSelectedRate(rate);
    setActiveFunderId(rate?.funder);
    setDialogOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const headers = [
    { key: "funder", label: "Council / Funder" },
    { key: "service_name", label: "Service & Code" },
    { key: "weekday_rate", label: "Weekday Rate" },
    { key: "weekend_rate", label: "Weekend Rate" },
    { key: "bank_holiday", label: "Bank Holiday" },
    { key: "unit_type", label: "Unit / Type" },
  ];

  const renderCell = (item, header) => {
    switch (header.key) {
      case "funder":
        return (
          <Typography
            variant="subtitle2"
            sx={{ color: "#1a237e", fontWeight: 700, fontSize: "0.8rem" }}
          >
            {getFunderName(item.funder)}
          </Typography>
        );

      case "service_name":
        return (
          <Box>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ fontSize: "0.78rem" }}
            >
              {item.service_name}
            </Typography>
            {item.service_code && (
              <Chip
                label={item.service_code}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  mt: 0.5,
                  bgcolor: alpha("#2469F0", 0.08),
                  color: "#2469F0",
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        );

      case "weekday_rate":
        return (
          <Typography
            sx={{ fontWeight: "bold", color: "#2e7d32", fontSize: "0.78rem" }}
          >
            £{parseFloat(item.weekday_rate || 0).toFixed(2)}
          </Typography>
        );

      case "weekend_rate":
        return (
          <Typography sx={{ color: "#666", fontSize: "0.78rem" }}>
            {item.weekend_rate
              ? `£${parseFloat(item.weekend_rate).toFixed(2)}`
              : "---"}
          </Typography>
        );

      case "bank_holiday":
        return (
          <Tooltip
            title={
              item.bank_holiday_pricing_type === "multiplier"
                ? "Multiplier"
                : "Fixed Override"
            }
            arrow
          >
            <Chip
              label={
                item.bank_holiday_pricing_type === "multiplier"
                  ? `${item.bank_holiday_multiplier}x`
                  : `£${item.bank_holiday_rate}`
              }
              size="small"
              variant="outlined"
              sx={{
                fontWeight: "bold",
                height: 22,
                fontSize: "0.7rem",
                color:
                  item.bank_holiday_pricing_type === "multiplier"
                    ? "#2469F0"
                    : "#F59E0B",
                borderColor:
                  item.bank_holiday_pricing_type === "multiplier"
                    ? alpha("#2469F0", 0.4)
                    : alpha("#F59E0B", 0.4),
              }}
            />
          </Tooltip>
        );

      case "unit_type":
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="caption"
              sx={{
                textTransform: "capitalize",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            >
              {item.rate_type}
            </Typography>
            <Tooltip title={`${item.unit_minutes} minute billing unit`} arrow>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: "#9e9e9e" }} />
            </Tooltip>
          </Stack>
        );

      default:
        return item[header.key] || "-";
    }
  };

  const customActions = (item) => (
    <Stack direction="row" spacing={0.25} justifyContent="center">
      <Tooltip title="Edit" arrow>
        <IconButton
          size="small"
          onClick={() => handleEditRate(item.id)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#F59E0B",
              bgcolor: alpha("#F59E0B", 0.06),
            },
          }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete" arrow>
        <IconButton
          size="small"
          onClick={() => handleDeleteClick(item.id)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#EF4444",
              bgcolor: alpha("#EF4444", 0.06),
            },
          }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <>
      <Paper
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          border: "1px solid #E2E8F0",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha("#2469F0", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MoneyIcon sx={{ color: "#2469F0", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                Master Rate Schedule
              </Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                Manage service pricing for all councils and funders
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={handleAddRate}
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
              bgcolor: "#2469F0",
              px: 3,
              py: 0.75,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            Add New Rate
          </Button>
        </Box>

        {/* TABLE */}
        <DataTable
          data={rates}
          headers={headers}
          renderCell={renderCell}
          customActions={customActions}
          loading={ratesLoading || fundersLoading}
          actionColumnLabel="Actions"
        />
      </Paper>

      <ConfirmModal
        show={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        handleConfirm={() => deleteRateMutation.mutate(deletingId)}
        title="Delete Rate Card?"
        body="This will permanently remove this pricing configuration. Are you sure?"
        isLoading={deleteRateMutation.isLoading}
        isDelete={true}
      />

      <FunderRateDialog
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        editData={selectedRate}
        funderId={activeFunderId}
      />
    </>
  );
};

export default FunderRateCards;